import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Bot,
  Brain,
  CheckCircle2,
  Globe,
  HeadphonesIcon,
  MessageSquare,
  Network,
  Shield,
  TrendingUp,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'roadmap', label: 'Roadmap' },
];

const AIWebChatPage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const overviewItems = [
    'Customer-facing websites',
    'Internal portals (IT, HR, Finance)',
    'Mobile-responsive design',
    'Custom branding & themes',
    'Natural language understanding',
    'Knowledge base + workflow automation',
    'Real-time API integrations',
    'Intelligent routing & escalation',
  ];

  const capabilities = [
    {
      icon: Globe,
      title: 'Embed Anywhere',
      desc: 'JavaScript snippet deployment, custom CSS theming, white-label branding, mobile-responsive design',
      gradient: 'from-teal-500 to-cyan-500',
    },
    {
      icon: Zap,
      title: 'Knowledge + Action',
      desc: 'Answer questions from your knowledge base and execute real workflows including password resets, provisioning and order updates.',
      gradient: 'from-cyan-500 to-blue-500',
    },
    {
      icon: Network,
      title: 'Omnichannel Context',
      desc: 'Preserve conversation history across web, email, voice, and SMS. Single customer view.',
      gradient: 'from-blue-500 to-indigo-500',
    },
    {
      icon: HeadphonesIcon,
      title: 'Intelligent Human Handoff',
      desc: 'Smart routing to the right agent or team with full context. Sentiment-based escalation triggers.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Shield,
      title: 'Governance & Controls',
      desc: 'Policy guardrails, PII masking, audit logs, approval workflows, compliance tracking',
      gradient: 'from-violet-500 to-purple-500',
    },
  ];

  const workflowSteps = [
    {
      num: '1',
      title: 'User Initiates Chat',
      desc: 'Customer or employee opens branded chat widget on website or portal',
    },
    {
      num: '2',
      title: 'Intent Recognition',
      desc: 'AI reads the message and determines whether the user needs information, an action taken, or escalation to a human agent.',
    },
    {
      num: '3',
      title: 'Knowledge or Automation',
      desc: 'Information requests pull from the knowledge base instantly. Action requests trigger the right backend workflow automatically.',
    },
    {
      num: '4',
      title: 'Real-Time Execution',
      desc: 'Workflows run through live API integrations covering password resets, order lookups, ticket creation and more.',
    },
    {
      num: '5',
      title: 'Intelligent Handoff',
      desc: 'If needed, route to human agent with full context, conversation history, and sentiment',
    },
    {
      num: '6',
      title: 'Analytics & Learning',
      desc: 'Log interaction, track outcomes, improve AI models, update knowledge base',
    },
  ];

  const problems = [
    {
      title: '1. Knowledge Without Action',
      desc: 'Can tell you how to reset a password but can\'t actually do it',
    },
    {
      title: '2. Disconnected Systems',
      desc: 'No integration with CRM, ITSM, billing, or identity platforms',
    },
    {
      title: '3. Abandoned Conversations',
      desc: 'Users leave chat to complete tasks elsewhere, losing context',
    },
    {
      title: '4. No Workflow Orchestration',
      desc: 'Can\'t trigger multi-step processes or coordinate across tools',
    },
    {
      title: '5. Poor Escalation Paths',
      desc: 'Agent handoffs lose context; no smart routing to right team',
    },
  ];

  const roadmapPhases = [
    {
      icon: Zap,
      title: 'Phase 1: Setup & Configuration (Week 1–2)',
      items: [
        'Deploy chat widget on website or portal',
        'Configure branding and themes',
        'Connect knowledge base',
        'Test basic Q&A functionality',
      ],
    },
    {
      icon: Workflow,
      title: 'Phase 2: Workflow Integration (Week 3–4)',
      items: [
        'Connect CRM, ITSM, identity systems',
        'Build 3–5 high-value automation workflows',
        'Configure escalation rules and routing',
        'Pilot with internal users or beta customers',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Phase 3: Optimization & Scale (Week 5–8)',
      items: [
        'Analyze deflection rates and conversation flows',
        'Expand automation coverage to additional use cases',
        'Train AI on organization-specific language',
        'Full production launch with monitoring',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="aiWebChat" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian AI Web Chat","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Convert website visitors with intelligent AI chat that qualifies leads, answers questions, and books demos automatically.","url":"https://digian.ai/ai-web-chat","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"How long does it take to go live?","acceptedAnswer":{"@type":"Answer","text":"Most customers have a branded chat widget live with basic Q&A in 1–2 weeks. Full workflow integrations typically complete within 4–8 weeks depending on the number of connected systems."}},{"@type":"Question","name":"Can it integrate with our existing helpdesk?","acceptedAnswer":{"@type":"Answer","text":"Yes. Digian.ai Web Chat integrates with ServiceNow, Zendesk, Salesforce, Freshdesk, Jira Service Management, and custom systems via REST APIs."}},{"@type":"Question","name":"How is the chat widget branded?","acceptedAnswer":{"@type":"Answer","text":"The widget supports full white label customization including your logo, color scheme, fonts and avatar. It can match your website or internal portal design precisely."}},{"@type":"Question","name":"What happens when the AI can't resolve an issue?","acceptedAnswer":{"@type":"Answer","text":"The AI escalates to a human agent with the full conversation context, sentiment score, and a suggested resolution so agents pick up mid-conversation without the user repeating themselves."}}]}]}} />
      {/* Sticky Timeline Sidebar (desktop only) */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-teal-600 border-teal-600 scale-125'
                      : isPast
                      ? 'bg-teal-400 border-teal-400'
                      : 'bg-white border-slate-300 group-hover:border-teal-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-teal-600 font-semibold'
                      : isPast
                      ? 'text-teal-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Opening Section */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-teal-500/10 via-cyan-500/10 to-blue-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              AI Web Chat Integration for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-cyan-600">
                Customer Care & Employee Support
              </span>
            </h1>
            <p className="text-xl text-teal-600 font-medium">
              Branded web chat powered by AI intelligence & end-to-end automation
            </p>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-teal-50 border-l-4 border-teal-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-teal-600">digian.ai AI Web Chat Integration</strong> deploys branded, AI-powered chat on any website or internal portal. Go beyond FAQ bots and resolve real issues through knowledge retrieval, workflow automation, and intelligent human handoff, all within a single unified interface.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Bot className="w-6 h-6 text-teal-600" /> Deploy AI-Powered Support Everywhere:
                  </h3>
                  <div className="space-y-3">
                    {overviewItems.map((item, i) => (
                      <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3 p-3 bg-white rounded-xl hover:translate-x-1 transition-transform hover:shadow-md">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-800">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-teal-200 mb-2">GOAL</h4>
                    <p className="text-lg">Move from answering questions to actually resolving issues, completely within the chat window</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-teal-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">Faster resolution, lower support costs, higher satisfaction scores</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              From <span className="text-teal-600">FAQ Bots</span> to Real Resolution
            </h2>
            <p className="text-lg text-slate-600 mb-10">Legacy chat widgets answer questions but can't solve problems. Five critical gaps:</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6">
                <h4 className="font-bold text-red-700 mb-2">{p.title}</h4>
                <p className="text-slate-700 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Core <span className="text-teal-600">Capabilities</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Everything you need for enterprise-grade web chat support</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${cap.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mb-3" />
                  <h4 className="text-lg font-bold mb-1">{cap.title}</h4>
                  <p className="text-sm text-white/90">{cap.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              How It <span className="text-teal-600">Works</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Six steps from first message to resolved outcome</p>
          </motion.div>

          <div className="flex flex-col gap-4">
            {workflowSteps.map((step, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 p-6 hover:border-teal-400 transition-colors">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white font-bold text-lg flex items-center justify-center flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section id="metrics" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Metrics That <span className="text-teal-600">Matter</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Proven outcomes for customer care and employee support</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Customer Care */}
            <motion.div {...fadeUp} className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <HeadphonesIcon className="w-7 h-7" /> Customer Care
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="text-4xl font-extrabold">3× Faster</div>
                  <div className="text-teal-100 mt-1">Resolution time vs. traditional chat</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">68%</div>
                  <div className="text-teal-100 mt-1">Deflection rate (automated resolutions)</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">24/7</div>
                  <div className="text-teal-100 mt-1">Availability without staffing costs</div>
                </div>
              </div>
            </motion.div>

            {/* Employee Support */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <Users className="w-7 h-7" /> Employee Support
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="text-4xl font-extrabold">40–50%</div>
                  <div className="text-cyan-100 mt-1">IT ticket deflection rate</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">&lt;2 min</div>
                  <div className="text-cyan-100 mt-1">Average resolution time for common requests</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">85%+</div>
                  <div className="text-cyan-100 mt-1">Employee satisfaction with self-service</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Best-Fit <span className="text-teal-600">Use Cases</span> & Integrations
            </h2>
            <p className="text-lg text-slate-600 mb-10">Purpose-built workflows for customer and employee support</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Customer Care */}
            <motion.div {...fadeUp} className="bg-white rounded-xl border-t-4 border-teal-500 p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-5">
                <MessageSquare className="w-6 h-6 text-teal-600" /> Customer Care
              </h3>
              <ul className="space-y-3 mb-6">
                {['Order status & tracking', 'Account management', 'Billing inquiries & payments', 'Product troubleshooting', 'Returns & refunds', 'Appointment scheduling'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <ArrowRight className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {['Salesforce', 'Zendesk', 'Shopify', 'Stripe'].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full border-2 border-teal-500 text-teal-600 text-sm font-semibold bg-white">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Employee Support */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-white rounded-xl border-t-4 border-cyan-500 p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-5">
                <Users className="w-6 h-6 text-cyan-600" /> Employee Support
              </h3>
              <ul className="space-y-3 mb-6">
                {['Password resets', 'Software access provisioning', 'HR policy questions', 'IT equipment requests', 'Expense approvals', 'Time-off requests'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <ArrowRight className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {['ServiceNow', 'Active Directory', 'Workday', 'Okta'].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full border-2 border-cyan-500 text-cyan-600 text-sm font-semibold bg-white">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Implementation <span className="text-teal-600">Roadmap</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Live in 8 weeks or less</p>
          </motion.div>

          <div className="flex flex-col gap-6 mb-16">
            {roadmapPhases.map((phase, i) => {
              const Icon = phase.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border-l-8 border-teal-500 p-8 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="font-bold text-slate-900 text-lg flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-teal-600" /> {phase.title}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {phase.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'How long does it take to go live?', a: 'Most customers have a branded chat widget live with basic Q&A in 1–2 weeks. Full workflow integrations typically complete within 4–8 weeks depending on the number of connected systems.' },
              { q: 'Can it integrate with our existing helpdesk?', a: 'Yes. Digian.ai Web Chat integrates with ServiceNow, Zendesk, Salesforce, Freshdesk, Jira Service Management, and custom systems via REST APIs.' },
              { q: 'How is the chat widget branded?', a: 'The widget supports full white label customization including your logo, color scheme, fonts and avatar. It can match your website or internal portal design precisely.' },
              { q: 'What happens when the AI can\'t resolve an issue?', a: 'The AI escalates to a human agent with the full conversation context, sentiment score, and a suggested resolution so agents pick up mid-conversation without the user repeating themselves.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-gradient-to-r from-teal-600 to-cyan-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Ready to Transform Your <span className="text-teal-200">Web Support?</span>
            </h2>
            <p className="text-xl text-teal-100">
              Deploy AI-powered chat that actually resolves issues, not just answers questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Schedule Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIWebChatPage;
